import { showSubscriptionError } from '../../../shared/ui/subscription-modal-emitter'
import { requestScheduledReviewEntry } from '../../../shared/ui/scheduled-review-emitter'
import { useLocation } from 'react-router-dom'
import { useRef, useState } from 'react'

import { getApiErrorMessage } from '../../../platform/api/api-error'
import { ReviewModeIcon } from '../../../shared/ui/ReviewModeIcon'
import type { ReviewMode } from '../domain/contracts'
import { useUpdateReviewModeMutation } from '../infrastructure/identity-api'
import './default-review-mode-settings.css'

type SettingsNotice = Readonly<{
  severity: 'error' | 'success'
  text: string
}>

export function DefaultReviewModeSettings({
  initialMode,
  onNotice,
}: {
  initialMode: ReviewMode
  onNotice: (notice: SettingsNotice | undefined) => void
}) {
  const { key: navigationKey } = useLocation()
  const entryPending = useRef(false)
  const [reviewMode, setReviewMode] = useState(initialMode)
  const [updateReviewMode, reviewModeState] = useUpdateReviewModeMutation()

  const saveReviewMode = async (nextMode: ReviewMode) => {
    if (
      nextMode === reviewMode ||
      reviewModeState.isLoading ||
      entryPending.current
    )
      return
    entryPending.current = true
    try {
      if (
        nextMode === 'scheduled' &&
        !(await requestScheduledReviewEntry(navigationKey))
      )
        return
    } finally {
      entryPending.current = false
    }

    const previousMode = reviewMode
    setReviewMode(nextMode)
    onNotice(undefined)
    try {
      await updateReviewMode(nextMode).unwrap()
      onNotice({
        severity: 'success',
        text: 'Основной режим повторения обновлён.',
      })
    } catch (error) {
      setReviewMode(previousMode)
      if (showSubscriptionError(error)) return
      onNotice({
        severity: 'error',
        text: getApiErrorMessage(
          error,
          'Не удалось изменить основной режим повторения.',
        ),
      })
    }
  }

  return (
    <article
      className="default-review-mode-settings"
      aria-labelledby="default-review-mode-title"
    >
      <div className="default-review-mode-settings__heading">
        <h2 id="default-review-mode-title">Основной режим повторения</h2>
        <p>
          Он будет открываться по умолчанию в личных сценариях. Режим групповых
          заданий не изменится.
        </p>
      </div>
      <fieldset className="default-review-mode-settings__options">
        <legend>Выберите основной режим</legend>
        <ReviewModeOption
          checked={reviewMode === 'free'}
          description="Изучайте карточки в своём темпе и меняйте их статус вручную."
          disabled={reviewModeState.isLoading}
          label="Свободный режим"
          mode="free"
          onChange={saveReviewMode}
        />
        <ReviewModeOption
          checked={reviewMode === 'scheduled'}
          description="Получайте очередь карточек на день по расписанию повторений."
          disabled={reviewModeState.isLoading}
          label="Повторение по расписанию"
          mode="scheduled"
          onChange={saveReviewMode}
        />
      </fieldset>
    </article>
  )
}

function ReviewModeOption({
  checked,
  description,
  disabled,
  label,
  mode,
  onChange,
}: {
  checked: boolean
  description: string
  disabled: boolean
  label: string
  mode: ReviewMode
  onChange: (mode: ReviewMode) => Promise<void>
}) {
  return (
    <label
      className={`default-review-mode-settings__option${checked ? ' is-selected' : ''}`}
    >
      <ReviewModeIcon mode={mode} />
      <span>
        <strong>{label}</strong>
        <small>{description}</small>
      </span>
      <input
        checked={checked}
        disabled={disabled}
        name="default-review-mode"
        onChange={() => void onChange(mode)}
        type="radio"
        value={mode}
      />
    </label>
  )
}
